import { exportToExcel } from 'exportToExcel'
import { getMultipleItemDetailsById, getQuestionsAndOptions } from 'gql/query'
import { examOnlineQuestionUploadApiIntegration } from 'helpers/exam-online/api-integration'
import { extractImagesAndCleanHTML } from 'helpers/question/extractImagesAndCleanHTML'
import { formatHtmlForExamOnline } from 'helpers/question/formatHtmlForExamOnline'
import { getDifficultyAndBlooms } from 'utility/exam-online-bloom-severity'

export interface IQuestionAnswerKey {
  id: string
  mark: string
  number: number
  option?: string
  answers?: string[]
  questionType: string
}
interface alphabetsType {
  [key: number]: string
}
export const findQuestionAnswers = async (questions: any, markPerQuestion: string, questionType: string, questionCount: number) => {
  try {
    if (questions && questions.length > 0) {
      let count = questionCount
      const questionAndAnswers: IQuestionAnswerKey[] = []
      const alphabets: alphabetsType = { 1: 'a', 2: 'b', 3: 'c', 4: 'd', 5: 'e', 6: 'f', 7: 'g', 8: 'h' }
      const selectedQuestionId: string[] = await Promise.all(
        questions.map(async (question: any) => {
          //for mcq questions
          if (questionType === 'MCQ') {
            const correctOptionIndex = await question.option_details.findIndex((option: any) => option?.option_flag === 'Right Answer')
            questionAndAnswers.push({
              id: question.id,
              mark: markPerQuestion,
              number: (count = count + 1),
              option: alphabets[correctOptionIndex + 1] || 'nil',
              questionType: questionType,
            })
          } //fill the blanks
          else if (questionType === 'Fill') {
            const fillBlankAnswers = question.option_details.map((option: any) => option.right_answer)
            questionAndAnswers.push({
              id: question.id,
              mark: markPerQuestion,
              number: (count = count + 1),
              answers: fillBlankAnswers,
              questionType: questionType,
            })
          } //for other options
          else {
            questionAndAnswers.push({
              id: question.id,
              mark: markPerQuestion,
              number: (count = count + 1),
              questionType: questionType,
            })
          }

          return question.id
        })
      )
      return { selectedQuestionId, questionAndAnswers, status: true }
    } else {
      return { status: false }
    }
  } catch (err) {
    console.log(err, 'error')
    return { status: false }
  }
}

export const paperExportHelper = async (exFormat: string, paperDetails: any, questionBankName: any = '') => {
  try {
    if (paperDetails?.status === 'Unfilled Paper') return false
    const questionAnswerKeys = paperDetails?.questionAnswerKeys
    if (!questionAnswerKeys || questionAnswerKeys.length === 0) return false
    const allQuestionIds = questionAnswerKeys.map((q: any) => q.id)
    const allSubsectionIds = paperDetails?.subSectionQuestions?.map((subsection: any) => subsection.id)
    const allQuestionIdsStringified = JSON.stringify(allQuestionIds)
    const allSubsectionDetails = await getMultipleItemDetailsById(
      'paper_template_sub_sections',
      allSubsectionIds,
      'id',
      `id name base_lang target_lang section{id name}`
    )
    if (!allSubsectionDetails || allSubsectionDetails.length === 0) return false
    let questionAndOptions = []
    if (paperDetails?.status === 'Approved') {
      const getQuestionDetailsFromSnapShot = await getQuestionsAndOptions(allQuestionIdsStringified, paperDetails?.id)
      if (getQuestionDetailsFromSnapShot.length === allQuestionIds.length) {
        const flattenedQuestionOptionsArray = getQuestionDetailsFromSnapShot.map((item: any) => {
          const { question, ...rest } = item
          return {
            ...rest,
            ...question,
          }
        })
        questionAndOptions = flattenedQuestionOptionsArray && flattenedQuestionOptionsArray.length > 0 ? flattenedQuestionOptionsArray : []
      }
    }
    if (questionAndOptions.length === 0) {
      questionAndOptions = await getQuestionsAndOptions(allQuestionIdsStringified)
    }

    if (questionAndOptions.length !== allQuestionIds.length) return false

    const optionsLetters = ['a', 'b', 'c', 'd', 'e']
    const exportData: any[] = []
    // Exam Online renders only a limited subset of HTML; sanitize so unsupported
    // tags (lists etc.) don't appear literally on the test-taking platform.
    const formatForTarget = (html: any) => (exFormat === 'examonline' ? formatHtmlForExamOnline(html) : html || '')

    for (const question of questionAnswerKeys) {
      const questionDetails = questionAndOptions.find((q: any) => q.id === question.id)
      //section details
      const subsectionDetails = allSubsectionDetails.find((sec: any) =>
        paperDetails?.subSectionQuestions?.some((item: any) => item.id === sec.id && item.questionIds.includes(question.id))
      )
      if (!questionDetails || !subsectionDetails) continue
      const isBilingual = subsectionDetails?.base_lang && subsectionDetails?.target_lang ? true : false
      const combinedTitle =
        isBilingual && questionDetails?.questionByTransilationOf?.title
          ? questionDetails?.questionByTransilationOf?.title + '<br/><br/>' + questionDetails?.title
          : questionDetails?.title
      const parsedTitle = await extractImagesAndCleanHTML(combinedTitle)
      if (exFormat === 'autoproctor') {
        const eachQuestion: any = {
          'Question Type': questionDetails?.question_type_id,
          Question: parsedTitle?.cleanedHTML || '',
          Opt1: '',
          Opt2: '',
          Opt3: '',
          Opt4: '',
          Opt5: '',
          CorrectAnsInd: '',
          Markdown: '',
          Points: question?.mark || '',
          'Time (in seconds)': '',
          'Image URL':
            parsedTitle?.imageUrls?.length > 1 ? parsedTitle?.imageUrls[parsedTitle?.imageUrls?.length - 1] : parsedTitle?.imageUrls?.join(',') || '',
          'Section Name': subsectionDetails?.section?.name || '',
          'Question Number': question?.number || '',
          'Question ID': questionDetails?.question_id || '',
        }

        // Fill options for MCQs
        if (questionDetails?.question_type_id === 'MCQ' && questionDetails?.option_details?.length >= 4) {
          // Cap to the first 4 options so any duplicate/extra entries never get exported
          const cappedOptionDetails = questionDetails.option_details.slice(0, 4)
          const optionTexts = await Promise.all(
            cappedOptionDetails.map(async (opt: any, idx: number) => {
              //if it bilingual
              const eachOptionText =
                isBilingual && questionDetails?.questionByTransilationOf?.option_details?.[idx]?.option_text
                  ? questionDetails?.questionByTransilationOf?.option_details[idx]?.option_text + '<br/><br/>' + opt?.option_text
                  : opt?.option_text
              const parsedOption = await extractImagesAndCleanHTML(eachOptionText)
              return parsedOption?.imageUrls?.length > 0
                ? parsedOption.imageUrls[parsedOption?.imageUrls?.length - 1]
                : parsedOption?.cleanedHTML || ''
            })
          )

          optionTexts.forEach((text, index) => {
            eachQuestion[`Opt${index + 1}`] = text
          })
        }
        eachQuestion['CorrectAnsInd'] = optionsLetters.indexOf(question?.option?.toLowerCase()) + 1 || ''
        exportData.push(eachQuestion)
      } else {
        // console.log(questionDetails,"Question Detailsss")
        const blooms_name = questionDetails?.bloom_level?.name
        const difficulty_name = questionDetails?.difficulty_level?.name
        const severityAndBtl = await getDifficultyAndBlooms(blooms_name, difficulty_name)
        const titleImg = parsedTitle?.imageUrls?.length >= 1 ? `<img src="${parsedTitle?.imageUrls[parsedTitle?.imageUrls?.length - 1]}">` : ''
        const questionData: any = {
          QuestionId: `${questionDetails?.question_id}` || `${questionDetails?.question_id}_${paperDetails?.paper_id}`,
          Language: questionDetails?.language || 'english',
          Question: `${formatForTarget(parsedTitle?.cleanedHTML)} <br/> ${titleImg}`,
          Type: questionDetails?.question_type_id === 'MCQ' ? 'Multiple_Choice_Single_Answer' : questionDetails?.question_type_id,
          Topic: subsectionDetails?.section?.name || '',
          Mark: Number(question?.mark) || 0,
          NegativeMark: 0,
          Severity: severityAndBtl?.severity || 0,
          Option1: '',
          Option2: '',
          Option3: '',
          Option4: '',
          CorrectOption: '',
          Reference: '',
          PassageTitle: '',
          BTL: severityAndBtl?.btl || '',
        }
        // Fill options for MCQs
        if (questionDetails?.question_type_id === 'MCQ' && questionDetails?.option_details?.length >= 4) {
          // Cap to the first 4 options so any duplicate/extra entries never get exported
          const cappedOptionDetails = questionDetails.option_details.slice(0, 4)
          const optionTexts = await Promise.all(
            cappedOptionDetails.map(async (opt: any, idx: number) => {
              //if it bilingual
              const eachOptionText =
                isBilingual && questionDetails?.questionByTransilationOf?.option_details?.[idx]?.option_text
                  ? questionDetails?.questionByTransilationOf?.option_details[idx]?.option_text + '<br/><br/>' + opt?.option_text
                  : opt?.option_text
              const parsedOption = await extractImagesAndCleanHTML(eachOptionText)
              return parsedOption?.imageUrls?.length > 0
                ? `<img src="${parsedOption?.imageUrls[parsedOption?.imageUrls?.length - 1]}">`
                : formatForTarget(parsedOption?.cleanedHTML)
            })
          )

          optionTexts.forEach((text, index) => {
            questionData[`Option${index + 1}`] = text
          })
        }
        questionData['CorrectOption'] = question?.option?.toUpperCase() || ''
        exportData.push(questionData)
      }
    }

    if (exportData.length !== questionAnswerKeys.length) return false
    if (exFormat === 'examonline') {
      const uploadQuestion = await examOnlineQuestionUploadApiIntegration(exportData, questionBankName)
      if (uploadQuestion) {
        return { status: true, message: 'All questions have been uploaded successfully to the online exam platform' }
      } else {
        return { status: false, message: 'Question upload failed,Please try again later!' }
      }
    }
    const fileName = paperDetails?.name
      ? `Paper_${paperDetails?.name}_${Math.round(new Date().getTime() / 1000)}_[${paperDetails?.paper_id}]-Autoproctor.xlsx`
      : `Paper_${paperDetails?.paper_id}-${paperDetails?.paper_smart_id}_${Math.round(new Date().getTime() / 1000)}-Autoproctor.xlsx`
    await exportToExcel(exportData, fileName)
    return true
  } catch (err) {
    console.log(err, 'ERR')
    return false
  }
}
